import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema, propertyMapSchema, systemPropertyKeySchema, propertyExpressionStringSchema, } from './common.schema';
import { ALERT_SEVERITIES } from '../constants/severities';
/**
 * Sensor — Detect's LogicModule equivalent (PRD §2.4). Versioned,
 * declarative documents executed by the collector's built-in protocol
 * engines. MVP types: `metric` (≈ DataSource) and `property`
 * (≈ PropertySource). Targeting is a property expression (≈ AppliesTo).
 */
export const SENSOR_TYPES = ['metric', 'property'];
export const SENSOR_ORIGINS = ['library', 'tenant'];
/** Protocols the collector's built-in engines execute at P0
 *  (PRD §2.1: ICMP, SNMP v1/v2c/v3, HTTP(S), SSH, WMI/WinRM, Perfmon). */
export const SENSOR_PROTOCOLS = [
    'icmp',
    'snmp',
    'http',
    'ssh',
    'wmi',
    'perfmon',
    /** Executed by the Meshtek cloud probe runner (functions), never by
     *  collectors — website checks and synthetic transactions (#5/#23). */
    'cloud',
];
/** Sensor key charset — shared by input, stored, and wire schemas so a
 *  key that validates anywhere is delimiter-safe everywhere. */
export const sensorKeySchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z][a-z0-9_.-]*$/, 'lowercase key: letters, digits, _.-');
/** Canonical datapoint name charset — delimiter-safe for dedup/row keys. */
export const datapointNameSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z][A-Za-z0-9_.]*$/, 'alphanumeric/underscore/dot, letter first');
export const DATAPOINT_TYPES = ['gauge', 'counter', 'derive'];
export const THRESHOLD_OPERATORS = ['>', '>=', '<', '<=', '==', '!='];
/**
 * Static threshold definition per severity. `null` for a severity means
 * "no threshold at that severity". Values are compared with `operator`
 * against the (post-math) datapoint value.
 */
export const thresholdSchema = z
    .object({
    operator: z.enum(THRESHOLD_OPERATORS),
    warning: z.number().finite().nullable(),
    error: z.number().finite().nullable(),
    critical: z.number().finite().nullable(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.warning === null && val.error === null && val.critical === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'at least one severity level must be set',
        });
    }
});
/** No-data handling for a datapoint (host down / collection stopped). */
export const noDataSchema = z
    .object({
    /** Raise an alert if no data arrives for this many collection intervals. */
    afterIntervals: z.number().int().min(2).max(60),
    severity: z.enum(ALERT_SEVERITIES),
})
    .strict();
export const datapointSchema = z
    .object({
    name: datapointNameSchema,
    description: z.string().max(1000).optional(),
    /**
     * Where the raw value comes from — protocol-specific selector:
     * SNMP OID, WMI property, Perfmon counter path, JSONPath into an HTTP
     * response, regex capture from SSH output, or an expression over other
     * datapoints (prefix `expr:`).
     */
    source: z.string().min(1).max(1024),
    type: z.enum(DATAPOINT_TYPES).default('gauge'),
    /** Optional post-processing expression (sandboxed; `value` is bound). */
    math: z.string().max(1024).optional(),
    units: z.string().max(32).optional(),
    threshold: thresholdSchema.nullable().default(null),
    noData: noDataSchema.nullable().default(null),
})
    .strict();
/** Instance discovery config for multi-instance sensors. */
export const sensorDiscoverySchema = z
    .object({
    enabled: z.boolean(),
    /** Protocol-specific discovery selector (e.g. SNMP walk root OID,
     *  WMI class, shell command). */
    source: z.string().min(1).max(1024),
    /** How often to re-run discovery, in minutes. */
    intervalMinutes: z.number().int().min(5).max(10080).default(60),
    /** Include filter expression over discovered instance properties.
     *  Syntax-validated at write time (same engine as appliesTo). */
    filter: propertyExpressionStringSchema.optional(),
    /** Automatically retire instances not seen for this many cycles. */
    retireAfterCycles: z.number().int().min(1).max(100).default(3),
})
    .strict();
export const graphDefinitionSchema = z
    .object({
    name: z.string().min(1).max(128),
    title: z.string().min(1).max(200),
    datapoints: z.array(datapointNameSchema).min(1).max(20),
    /** vertical-axis label */
    verticalLabel: z.string().max(64).optional(),
})
    .strict();
export const sensorCollectionSchema = z
    .object({
    protocol: z.enum(SENSOR_PROTOCOLS),
    intervalSeconds: z.number().int().min(15).max(86400).default(300),
    timeoutSeconds: z.number().int().min(1).max(300).default(30),
    /**
     * Protocol-specific collection parameters (SNMP OIDs and version
     * hints, HTTP url/headers template, WMI namespace/class, SSH command,
     * Perfmon object). Validated in depth by the collector engine for the
     * declared protocol; the control plane treats it as opaque config.
     * Never contains credentials — those are device properties resolved
     * locally by the collector (encrypted at rest on site, never uploaded).
     */
    config: z.record(z.unknown()),
})
    .strict();
export const sensorInputSchema = z
    .object({
    /** Stable key, e.g. `win.cpu`, `net.interfaces` — unique per tenant. */
    key: sensorKeySchema,
    displayName: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    type: z.enum(SENSOR_TYPES),
    /** Property expression selecting the resources this sensor applies to
     *  (AppliesTo equivalent), e.g. `system.os == "windows" && custom.env != "lab"`.
     *  Syntax-validated at write time — a typo'd expression is a 400, not a
     *  silent never-match. */
    appliesTo: propertyExpressionStringSchema,
    collection: sensorCollectionSchema,
    discovery: sensorDiscoverySchema.nullable().default(null),
    datapoints: z.array(datapointSchema).max(200).default([]),
    graphs: z.array(graphDefinitionSchema).max(50).default([]),
    /** For `property` sensors: properties the sensor sets (system.*). */
    setsProperties: z.array(systemPropertyKeySchema).max(100).default([]),
    tags: z.array(z.string().min(1).max(64)).max(20).default([]),
})
    .strict()
    .superRefine((val, ctx) => sensorTypeRules(val, ctx));
/**
 * Sensor-type disjointness and internal-consistency rules, applied to both
 * the input schema and the stored schema. The sensor types are disjoint
 * module kinds: metric sensors collect datapoints, property sensors set
 * properties — mixed documents are rejected, not tolerated.
 */
export function sensorTypeRules(val, ctx) {
    if (val.type === 'metric') {
        if (val.datapoints.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['datapoints'],
                message: 'metric sensors must define at least one datapoint',
            });
        }
        if (val.setsProperties.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['setsProperties'],
                message: 'metric sensors must not declare setsProperties',
            });
        }
    }
    if (val.type === 'property') {
        if (val.setsProperties.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['setsProperties'],
                message: 'property sensors must declare the properties they set',
            });
        }
        if (val.datapoints.length > 0 || val.graphs.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['datapoints'],
                message: 'property sensors must not define datapoints or graphs',
            });
        }
    }
    const dpNames = new Set(val.datapoints.map((d) => d.name));
    if (dpNames.size !== val.datapoints.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['datapoints'],
            message: 'datapoint names must be unique within a sensor',
        });
    }
    for (const graph of val.graphs) {
        for (const dp of graph.datapoints) {
            if (!dpNames.has(dp)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['graphs'],
                    message: `graph "${graph.name}" references unknown datapoint "${dp}"`,
                });
            }
        }
    }
}
export const sensorSchema = z
    .object({
    key: sensorKeySchema,
    displayName: z.string().min(1).max(200),
    description: z.string().max(4000).nullable(),
    type: z.enum(SENSOR_TYPES),
    origin: z.enum(SENSOR_ORIGINS),
    /** Monotonic version; bumped on every definition change. Collectors
     *  report the exact version they run (tech-arch §6). */
    version: z.number().int().min(1),
    /** For library sensors: the upstream library version this doc mirrors,
     *  enabling diff-before-update on module upgrades (PRD §2.4). */
    libraryVersion: z.number().int().min(1).nullable(),
    /** Library sensors: fingerprint (sha256 hex) of the library DEFINITION
     *  this document was installed/upgraded from. The sensor is "pristine"
     *  while its current definition still hashes to this; a tenant edit
     *  makes it differ, and the automatic library rollout then leaves the
     *  sensor alone (pinned) instead of overwriting the tenant's tuning.
     *  null on tenant sensors, and on library sensors installed before this
     *  field existed (classified from the frozen shipped-definition history
     *  at the next upgrade). */
    libraryBaseFingerprint: z.string().regex(/^[0-9a-f]{64}$/).nullable().default(null),
    /** Library sensors pinned because the tenant customized them: the
     *  library version they have NOT received. null = nothing pending. */
    libraryUpdateAvailable: z.number().int().min(1).nullable().default(null),
    /** Same syntax validation as the input schema — a stored sensor with
     *  an unevaluable expression must not parse. */
    appliesTo: propertyExpressionStringSchema,
    collection: sensorCollectionSchema,
    discovery: sensorDiscoverySchema.nullable(),
    datapoints: z.array(datapointSchema).max(200),
    graphs: z.array(graphDefinitionSchema).max(50),
    setsProperties: z.array(systemPropertyKeySchema).max(100),
    tags: z.array(z.string().min(1).max(64)).max(20),
    enabled: z.boolean(),
})
    .extend(baseEntityFields)
    .strict()
    // Stored docs enforce the same type-disjointness rules as inputs.
    .superRefine((val, ctx) => {
    sensorTypeRules(val, ctx);
    // Library-mirror contract: library sensors track their upstream
    // version; tenant-authored sensors have no upstream.
    if (val.origin === 'library' && val.libraryVersion === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['libraryVersion'],
            message: 'library sensors must record the upstream libraryVersion',
        });
    }
    if (val.origin === 'tenant' && val.libraryVersion !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['libraryVersion'],
            message: 'tenant sensors must not carry a libraryVersion',
        });
    }
    if (val.origin === 'tenant' &&
        (val.libraryBaseFingerprint !== null || val.libraryUpdateAvailable !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['libraryBaseFingerprint'],
            message: 'tenant sensors must not carry library tracking fields',
        });
    }
});
/**
 * SensorAssignment — materialized sensor→resource application produced by
 * evaluating `appliesTo` expressions against resource properties
 * (tech-arch §4.2). Overrides carry group/resource-level threshold and
 * interval customization (PRD §2.5 inheritance).
 */
export const assignmentOverridesSchema = z
    .object({
    intervalSeconds: z.number().int().min(15).max(86400).optional(),
    /** Per-datapoint threshold overrides keyed by datapoint name. */
    thresholds: z.record(datapointNameSchema, thresholdSchema).optional(),
    /** Disable specific datapoints on this resource. */
    disabledDatapoints: z.array(datapointNameSchema).max(200).optional(),
})
    .strict();
export const sensorAssignmentSchema = z
    .object({
    resourceId: idSchema,
    sensorId: idSchema,
    sensorKey: sensorKeySchema,
    sensorVersion: z.number().int().min(1),
    status: z.enum(['active', 'disabled']),
    /** How the assignment came to be. */
    source: z.enum(['auto', 'manual']),
    matchedAt: isoDateTimeSchema,
    overrides: assignmentOverridesSchema.nullable(),
    /** Snapshot of the resource properties that satisfied appliesTo —
     *  kept for explainability ("why is this applied?"). */
    matchedProperties: propertyMapSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict();
//# sourceMappingURL=sensor.schema.js.map